import React, { useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField, Button, Alert, Box, Typography, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { api } from "../../services/api";
import { useAuth } from "../context/AuthContext";
import { FiberManualRecord, Warning } from '@mui/icons-material';

const ConfirmDeleteAccountModal = ({ open, handleClose: onClose, onSuccess }) => {
  const { user, logoutWithRedirect } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleClose = (event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    if (onClose) onClose();
  };

  const handleSubmit = async () => {
    if (email !== user?.usuario) {
      setError('El email ingresado no coincide con tu cuenta');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.usuarios.BajaUsuario(email);

      if (response.status === 'success') {
        // si la baja es exitosa, cerramos el modal y logout
        handleClose();
        if (onSuccess) onSuccess();

        setTimeout(() => {
          logoutWithRedirect("/");
        }, 2000);
      } else {
        setError(response.message || 'Error al procesar la solicitud');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Ocurrió un error al dar de baja la cuenta');
      console.error('Error al dar de baja la cuenta:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? null : handleClose} fullWidth maxWidth="sm">
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 4 }}>
        <Warning sx={{ color: '#ee032a', fontSize: 48, mb: 1 }} />
        <DialogTitle sx={{ color: 'black', fontWeight: 700, textAlign: 'center', p: 0, mb: 1 }}>
          Eliminar tu cuenta
        </DialogTitle>
        <Typography sx={{ color: '#444', textAlign: 'center', fontWeight: 400, mb: 2 }}>
          Esta acción es irreversible. Todos tus datos serán eliminados permanentemente.
        </Typography>
      </Box>
      <DialogContent sx={{ pt: 0 }}>
        <Typography sx={{ fontSize: "14px", mb: 1, color: '#444', textAlign: 'start' }}>
          Estás a punto de solicitar la eliminación permanente de tu cuenta y toda la información asociada a ella, incluyendo:
        </Typography>
        <List dense sx={{ mb: 1, pl: 4 }}>
          <ListItem sx={{ py: 0 }}>
            <ListItemIcon sx={{ minWidth: 24 }}>
              <FiberManualRecord sx={{ fontSize: 8, color: 'black' }} />
            </ListItemIcon>
            <ListItemText sx={{ fontFamily: "Bricolage Grotesque, sans serif" }} primary="Tu perfil y datos personales." />
          </ListItem>
          <ListItem sx={{ py: 0 }}>
            <ListItemIcon sx={{ minWidth: 24 }}>
              <FiberManualRecord sx={{ fontSize: 8, color: 'black' }} />
            </ListItemIcon>
            <ListItemText sx={{ fontFamily: "Bricolage Grotesque, sans serif" }} primary="Tu historial de eventos creados y asistidos." />
          </ListItem>
          <ListItem sx={{ py: 0 }}>
            <ListItemIcon sx={{ minWidth: 24 }}>
              <FiberManualRecord sx={{ fontSize: 8, color: 'black' }} />
            </ListItemIcon>
            <ListItemText sx={{ fontFamily: "Bricolage Grotesque, sans serif" }} primary="Tus compras y tickets." />
          </ListItem>
        </List>
        <Typography sx={{ fontSize: "14px", mb: 1, color: '#222', fontWeight: 600, textAlign: 'start' }}>
          Esta acción no se puede deshacer. Si decides volver a usar nuestros servicios en el futuro, deberás crear una nueva cuenta desde cero.
        </Typography>
        <Typography sx={{ fontSize: "14px", mb: 1, color: '#444', textAlign: 'start' }}>
          Para confirmar, por favor ingresa tu email:
        </Typography>
        <TextField
          autoFocus
          margin="dense"
          type="email"
          fullWidth
          variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          sx={{
            fontFamily: "Bricolage Grotesque",
            mb: 1,
            '& .MuiInputBase-input-MuiOutlinedInput-input': {
              fontFamily: "Bricolage Grotesque",
            },
            '& label.Mui-focused': {
              color: '#5656FF',
              fontFamily: "Bricolage Grotesque"
            },
            '& .MuiOutlinedInput-root': {
              height: "40px",

              '& fieldset': {
                borderColor: '#B2BAC2',
              },
              '&:hover fieldset': {
                borderColor: '#5656ff',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#5656ff',
              },
            }
          }}
        />
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Button
          onClick={handleSubmit}
          color="error"
          variant="contained"
          disabled={loading || !email}
          sx={{
            marginLeft: "0 !important",
            backgroundColor: "#af2e2e",
            color: "white",
            padding: "5px 10px",
            borderRadius: "4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: window.innerWidth <= 576 ? "13px" : "14px",
            width: "100%"
          }}
        >
          {loading ? 'Procesando...' : 'Sí, eliminar mi cuenta'}
        </Button>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          sx={{
          border: "1px solid #5656FF",
          color: "#5656FF",
          padding: "5px 10px",
          borderRadius: "4px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: window.innerWidth <= 576 ? "13px" : "14px",
          width: "100%",
        }}
        >
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDeleteAccountModal;